import { inject, Injectable, NgZone } from '@angular/core';
import { IonContent, Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';

export type DeviceOrientation = 'portrait' | 'landscape' | 'unknown';
type ScrollDirection = 'TOP' | 'BOTTOM';

@Injectable({
  providedIn: 'root',
})
export class DeviceConfigService {
  #platform: Platform = inject(Platform);
  #ngZone: NgZone = inject(NgZone);
  #scrollContent?: IonContent;

  orientation$: BehaviorSubject<DeviceOrientation> = new BehaviorSubject<DeviceOrientation>(this.getOrientation());

  constructor() {
    void this.#initialize();
  }

  setScrollContent(content: IonContent) {
    this.#scrollContent = content;
  }

  getOrientation(): DeviceOrientation {
    if (this.#platform.isPortrait()) {
      return 'portrait';
    } else if (this.#platform.isLandscape()) {
      return 'landscape';
    } else {
      return 'unknown';
    }
  }

  async #initialize(): Promise<void> {
    await this.#platform.ready();
    this.#initializeKeyboardListeners();
    this.#platform.resize.subscribe(() => {
      this.#updateOrientation();
    });
  }

  #initializeKeyboardListeners() {
    window.addEventListener('ionKeyboardDidShow', () => {
      void this.#scrollToBottom();
    });
    window.addEventListener('ionKeyboardDidHide', () => {
      void this.#scrollToTop();
    });
  }

  #scrollToBottom(): void {
    void this.#scrollTo('BOTTOM');
  }

  #scrollToTop(): void {
    void this.#scrollTo('TOP');
  }

  #updateOrientation(): void {
    this.#ngZone.run(() => {
      this.orientation$.next(this.getOrientation());
    });
  }

  async #scrollTo(dir: ScrollDirection): Promise<void> {
    if (!this.#scrollContent) {
      return;
    }
    const scrollable: HTMLElement = await this.#scrollContent.getScrollElement();
    scrollable.scrollTo({
      top: dir === 'BOTTOM' ? scrollable.scrollHeight : 0,
      behavior: 'smooth',
    });
  }
}
